import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  FormControl,
  TextField,
  Theme,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import {
  Body,
  BottomActionArea,
  BottomActionButton,
  DigitalSign,
  FullScreenLoader,
  Header,
  InputFieldWrapper,
  PatientSummaryAccordion,
  scrollToTop,
  NavigateBeforeButton,
  ExtraWhiteSpace,
  AuthRequiredModal,
  ContentContainer as BaseContentContainer,
  PatientSummary as BasePatientSummary,
} from "@components";
import {
  DocumentCreationLoaderTitle,
  MedicalCertificate,
  Patient,
  RequestStatus,
  RequestType,
  patientDefaultValues,
} from "@interfaces";
import { useDoctorContext } from "@contexts";
import { useCreateDocument, useRequestData } from "@hooks";
import { separateDoctorSpecialty } from "@utils";
import { format } from "rut.js";
import { DateField } from "@mui/x-date-pickers/DateField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { esES } from "@mui/x-date-pickers/locales";
import es from "date-fns/locale/es";

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  gap: 24,
}));

const PatientSummary = styled(BasePatientSummary)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const HeaderContainer = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  paddingBottom: 0,
  justifyContent: "center",
  [theme.breakpoints.up("md")]: {
    justifyContent: "space-between",
    padding: 24,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  [theme.breakpoints.down("md")]: {
    paddingBottom: 0,
  },
}));

const Content = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  alignItems: "normal",
  [theme.breakpoints.up("md")]: {
    padding: 24,
    maxWidth: 1200,
    paddingTop: 0,
    paddingBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 0,
  },
}));

const PatientContent = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 328,
  },
}));

const PrescriptionContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  paddingTop: 0,
}));

const PatientSummaryAccordionContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const FormContainer = styled("form")({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: "20px",
});

const Input = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.background.white,
  fontSize: "1rem",
  fontWeight: 400,
  "& .MuiInputBase-root": {
    padding: "8px 12px",
    fontSize: "1rem",
    fontWeight: 400,
  },
  "& textarea": {
    minHeight: "200px",
  },
}));

const StyledDateField = styled(DateField)<{ error: boolean }>(({ theme }) => ({
  backgroundColor: theme.palette.background.white,
  ...(props) =>
    props.error && {
      border: "1px solid red",
      borderRadius: "4px",
    },
}));

interface LocationState {
  repeatedDocumentDetails?: string;
}

export function MedicalCertificateCreate() {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const doctor = useDoctorContext();
  const navigate = useNavigate();
  const { requestId = "" } = useParams();
  const { data: requestData, isPending: isPendingRequestData } =
    useRequestData();
  const location = useLocation();
  const { repeatedDocumentDetails }: LocationState = location.state || {};
  const [showFeaAuthModal, setShowFeaAuthModal] = useState<boolean>(false);

  const patient: Patient = useMemo(() => {
    return requestData?.patient ?? patientDefaultValues;
  }, [requestData]);

  const isDraft = useMemo(() => {
    return requestData?.request.status === RequestStatus.DRAFT;
  }, [requestData]);

  const placeholderComponents = useMemo(() => {
    const { mekidocAccessTitle, specialties } = separateDoctorSpecialty(
      doctor.speciality,
    );
    const title = specialties !== "" ? specialties.trim() : mekidocAccessTitle;
    const patientDocumentNumber = patient.rut
      ? format(patient.rut)
      : patient.passport || "";
    const dynamicPlaceholder = `Yo, ${doctor.name} ${doctor.lastName} (${title}), certifico que el paciente ${patient.name} ${patient.firstSurname} ${patient.secondSurname} (${patientDocumentNumber})`;
    const placeholderPostfix =
      "Extiendo el presente certificado para los fines que el paciente estime conveniente.";
    return { dynamicPlaceholder, placeholderPostfix };
  }, [doctor, patient]);

  const certificatePlaceholder = useMemo(() => {
    if (!patient.name) return "";
    return `${placeholderComponents.dynamicPlaceholder} 


${placeholderComponents.placeholderPostfix}`;
  }, [placeholderComponents, patient.name]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<MedicalCertificate>({
    defaultValues: {
      effectiveDate: new Date().toISOString(),
      documentDetails: repeatedDocumentDetails ?? certificatePlaceholder,
    },
  });

  const { mutate: createDocument, isPending } = useCreateDocument(
    RequestType.MEDICAL_CERTIFICATE,
  );
  const [showDigitalSign, setShowDigitalSign] = useState(false);
  const effectiveDate = watch("effectiveDate");
  const documentDetails = watch("documentDetails");

  const handleDocumentDataSubmit = useCallback(() => {
    if (!doctor.isFeaAuthenticated) {
      setShowFeaAuthModal(true);
      return;
    }
    setShowDigitalSign(true);
    scrollToTop();
  }, [doctor]);

  const onDigitalSignSubmit = useCallback(async () => {
    setShowDigitalSign(false);
    const textToFilter = [
      placeholderComponents.dynamicPlaceholder,
      placeholderComponents.placeholderPostfix,
    ];
    let managementSummary = documentDetails;
    textToFilter.forEach((text) => {
      managementSummary = managementSummary.replace(text, "");
    });
    createDocument({
      document: { effectiveDate, documentDetails },
      managementSummary: managementSummary.trim(),
    });
  }, [placeholderComponents, effectiveDate, documentDetails, createDocument]);

  const handleBack = useCallback(() => {
    if (isDraft) {
      navigate(`/app/nuevo-documento/paciente`, {
        state: { documentType: RequestType.MEDICAL_CERTIFICATE },
      });
    } else {
      navigate(`/app/solicitudes/${requestId}/gestionar/seleccionar-documento`);
    }
  }, [navigate, isDraft, requestId]);

  useEffect(() => {
    function updatePlaceholder() {
      if (!documentDetails)
        setValue(
          "documentDetails",
          repeatedDocumentDetails ?? certificatePlaceholder,
        );
    }
    updatePlaceholder();
  }, [
    documentDetails,
    repeatedDocumentDetails,
    certificatePlaceholder,
    setValue,
  ]);

  if (showDigitalSign) {
    return (
      <DigitalSign
        documentType={RequestType.MEDICAL_CERTIFICATE}
        handleCreateDocument={onDigitalSignSubmit}
        handleBack={() => setShowDigitalSign(false)}
      />
    );
  }

  if (isPendingRequestData) {
    return (
      <>
        <Header
          title={isMobile ? "Certificado médico" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando información del paciente..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  if (isPending)
    return (
      <>
        <Header
          title={isMobile ? "Certificado médico" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader
              title={
                DocumentCreationLoaderTitle[RequestType.MEDICAL_CERTIFICATE]
              }
            />
          </Body>
        </ContentContainer>
      </>
    );

  return (
    <>
      <Header
        title={isMobile ? "Certificado médico" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h5" fontWeight={"medium"}>
            Certificado médico
          </Title>
        </HeaderContainer>
        <Content>
          <PatientContent>
            <PatientSummary
              patient={patient}
              prevRoute={`/app/solicitudes/${requestId}/certificado/crear`}
              requestId={requestId}
              canEdit={isDraft}
            />
            <PatientSummaryAccordionContainer>
              <PatientSummaryAccordion
                patient={patient}
                prevRoute={`/app/solicitudes/${requestId}/certificado/crear`}
                requestId={requestId}
                isOpen={isDraft}
                canEdit={isDraft}
              />
            </PatientSummaryAccordionContainer>
          </PatientContent>
          <PrescriptionContent>
            <FormContainer onSubmit={handleSubmit(handleDocumentDataSubmit)}>
              <InputFieldWrapper
                label="Fecha de certificado"
                error={errors.effectiveDate?.message}
              >
                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={es}
                    localeText={
                      esES.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <Controller
                      name="effectiveDate"
                      control={control}
                      defaultValue={undefined}
                      rules={{
                        required: "Debes seleccionar una fecha.",
                        validate: (value) => {
                          const date = new Date(value);
                          if (date.toString() === "Invalid Date") {
                            return "Fecha incompleta o inválida";
                          }
                          if (date.getFullYear() < 1900) {
                            return "La fecha es demasiado antigua";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <StyledDateField
                          minDate={new Date("1900-01-01")}
                          value={value ? new Date(value) : null}
                          onChange={(date) => onChange(date)}
                          clearable={true}
                          format="dd/MM/yyyy"
                          error={
                            errors.effectiveDate?.message ===
                            "Debes seleccionar una fecha."
                          }
                          slotProps={{
                            // Disable the blue border on focus when there is an error
                            ...(errors.effectiveDate &&
                              errors.effectiveDate.message ===
                                "Debes seleccionar una fecha." && {
                                textField: {
                                  focused: false,
                                },
                              }),
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </InputFieldWrapper>

              <InputFieldWrapper
                label="Detalle certificado médico"
                error={errors.documentDetails?.message}
              >
                <FormControl fullWidth>
                  <Input
                    multiline
                    error={!!errors.documentDetails}
                    {...register("documentDetails", {
                      required:
                        "Debes agregar la razón e indicaciones del certificado médico.",
                    })}
                  />
                </FormControl>
              </InputFieldWrapper>

              <ExtraWhiteSpace />

              <BottomActionArea>
                <BottomActionButton type="submit">Siguiente</BottomActionButton>
              </BottomActionArea>
            </FormContainer>
          </PrescriptionContent>
        </Content>
        <AuthRequiredModal
          requestId={requestId}
          isOpen={showFeaAuthModal}
          onClose={() => setShowFeaAuthModal(false)}
        />
      </ContentContainer>
    </>
  );
}
